import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Building2, Briefcase, Calendar } from 'lucide-react';

import { getEmployeeCareerHistory } from '../../../services/employeeService';
import { formatDate } from '../../../utils/Utils';

export default function CareerHistoryTab({ employeeId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEmployeeCareerHistory(employeeId)
      .then((res) => {
        const data = res.data?.data || [];
        const sorted = [...data].sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
        setRecords(sorted);
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <LoadingText>{t('common.loading') || '...'}</LoadingText>;
  if (records.length === 0) return <EmptyText>{t('common.noRecords') || '-'}</EmptyText>;

  return (
    <Container>
      <Timeline>
        {records.map((record, index) => {
          const isActive = record.isActive && !record.endDate;
          const startStr = formatDate(record.startDate, lang);
          const endStr = record.endDate
            ? formatDate(record.endDate, lang)
            : t('employee.continuingPosition');

          return (
            <TimelineItem key={record.id || index}>
              <TimelineDot $active={isActive} />
              {index < records.length - 1 && <TimelineLine />}

              <TimelineCard $active={isActive}>
                <CardHeader>
                  <TitleName>{record.titleName || '-'}</TitleName>
                  <StatusBadge $active={isActive}>
                    {isActive ? t('employee.active') : t('employee.past')}
                  </StatusBadge>
                </CardHeader>

                <CardDetails>
                  <DetailRow>
                    <Building2 size={14} />
                    <span>{record.departmentName || '-'}</span>
                  </DetailRow>
                  <DetailRow>
                    <Briefcase size={14} />
                    <span>{record.positionCode || '-'}</span>
                  </DetailRow>
                  <DetailRow>
                    <Calendar size={14} />
                    <span>{startStr} - {endStr}</span>
                  </DetailRow>
                </CardDetails>

                {record.assignmentType && (
                  <AssignmentType>{record.assignmentType}</AssignmentType>
                )}
                {record.changeReason && (
                  <ChangeReason>{record.changeReason}</ChangeReason>
                )}
              </TimelineCard>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Container>
  );
}

const Container = styled.div`
  padding: 8px 0;
`;

const LoadingText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const EmptyText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  padding: 40px;
  text-align: center;
`;

const Timeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-left: 24px;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 24px;
  padding-left: 24px;
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -6px;
  top: 8px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $active, theme }) => $active ? theme.colors.primary.main : '#cbd5e1'};
  border: 2px solid #fff;
  z-index: 1;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: -1px;
  top: 20px;
  bottom: 0;
  width: 2px;
  background: #E8ECF0;
`;

const TimelineCard = styled.div`
  padding: 16px 20px;
  background: ${({ $active, theme }) => $active ? (theme.colors.primary.main + '08') : '#fff'};
  border-radius: 6px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary.main + '30' : '#E8ECF0'};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const TitleName = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusBadge = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
  background: ${({ $active }) => $active ? '#dcfce7' : '#f1f5f9'};
  color: ${({ $active }) => $active ? '#16a34a' : '#64748b'};
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
`;

const AssignmentType = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 10px;
  font-weight: 500;
`;

const ChangeReason = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
  margin-top: 4px;
`;
